// Self-contained admin/user portal mounted at /admin and /invite/<token>.
// Deliberately router-free (the rest of this app hand-rolls routing off
// window.location too). Navigation between admin views is plain full-page
// navigation — fine for a low-traffic back office, and avoids a new dep.
//
// Phase 1 scope: auth (login / invite-accept / logout) + a minimal dashboard
// that lists the domains you can manage. Domain editing (P2), reports (P3),
// and settings (P4) slot into the dashboard later.

import React, { useState, useEffect, useCallback } from 'react';
import './AdminApp.css';
import { adminPost } from './api.js';

function Shell({ children, user, onLogout, nav }) {
  const showNav = nav && nav.isAdmin;
  return (
    <div className="admin">
      <header className="admin-header">
        <div className="admin-header-inner">
          <a href="/admin" className="admin-logo">Ollie Mail</a>
          <span className="admin-header-tag">admin</span>
          {user && (
            <div className="admin-header-user">
              <span>{user.email}{user.role === 'admin' ? ' · admin' : ''}</span>
              <button className="admin-link-btn" onClick={onLogout}>Log out</button>
            </div>
          )}
        </div>
        {showNav && (
          <div className="admin-nav">
            <div className="admin-nav-inner">
              {[['domains', 'Domains'], ['promos', 'Promos'], ['users', 'Users'], ['settings', 'Settings']].map(([key, label]) => (
                <button key={key}
                        className={'admin-nav-link' + (nav.active === key ? ' active' : '')}
                        onClick={() => nav.onNavigate(key)}>{label}</button>
              ))}
            </div>
          </div>
        )}
      </header>
      <main className="admin-main">{children}</main>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="admin-field">
      <span>{label}</span>
      <input {...props} />
    </label>
  );
}

function Login({ onAuthed }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    const { ok, data } = await adminPost('/admin/login', { email, password });
    setBusy(false);
    if (ok && data.result === 'success') { onAuthed(data.user); }
    else { setError(data.message || 'Login failed'); }
  };

  return (
    <Shell>
      <div className="admin-card admin-narrow">
        <h1>Sign in</h1>
        <form onSubmit={submit}>
          <Field label="Email" type="email" autoComplete="username" value={email}
                 onChange={(e) => setEmail(e.target.value)} required />
          <Field label="Password" type="password" autoComplete="current-password" value={password}
                 onChange={(e) => setPassword(e.target.value)} required />
          {error && <p className="admin-error">{error}</p>}
          <button className="admin-btn" disabled={busy} type="submit">
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </Shell>
  );
}

function InviteAccept({ token }) {
  const [state, setState] = useState({ loading: true });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await adminPost('/admin/invite/lookup', { token });
      if (data.valid) {
        setState({ loading: false, valid: true, ...data });
      } else {
        setState({ loading: false, valid: false });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (state.needsPassword) {
      if (password.length < 8) { setError('Choose a password of at least 8 characters.'); return; }
      if (password !== confirm) { setError('Passwords do not match.'); return; }
    }
    setBusy(true);
    const { ok, data } = await adminPost('/admin/invite/accept', { token, password });
    setBusy(false);
    if (ok && data.result === 'success') { window.location.assign('/admin'); }
    else { setError(data.message || 'Could not accept invite'); }
  };

  if (state.loading) return <Shell><div className="admin-card admin-narrow">Loading…</div></Shell>;
  if (!state.valid) {
    return (
      <Shell>
        <div className="admin-card admin-narrow">
          <h1>Invite not valid</h1>
          <p className="admin-muted">This invite link is invalid or has expired. Ask an administrator to send a new one.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="admin-card admin-narrow">
        <h1>Welcome to Ollie Mail</h1>
        <p className="admin-muted">
          {state.role === 'admin'
            ? <>You're being set up as an <strong>administrator</strong>.</>
            : <>You've been invited to manage <strong>{state.domain}</strong>.</>}
        </p>
        <form onSubmit={submit}>
          <Field label="Email" type="email" value={state.email} readOnly disabled />
          {state.needsPassword ? (
            <>
              <Field label="Choose a password" type="password" autoComplete="new-password"
                     value={password} onChange={(e) => setPassword(e.target.value)} required />
              <Field label="Confirm password" type="password" autoComplete="new-password"
                     value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </>
          ) : (
            <p className="admin-muted">You already have an account — accepting will add this domain to it.</p>
          )}
          {error && <p className="admin-error">{error}</p>}
          <button className="admin-btn" disabled={busy} type="submit">
            {busy ? 'Setting up…' : (state.needsPassword ? 'Set password & continue' : 'Accept invite')}
          </button>
        </form>
      </div>
    </Shell>
  );
}

function Dashboard({ user, onLogout, onOpen, onAdd, onReport, nav }) {
  const [domains, setDomains] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await adminPost('/admin/domains/list', {});
      setDomains(data.domains || []);
    })();
  }, []);

  return (
    <Shell user={user} onLogout={onLogout} nav={nav}>
      <div className="admin-card">
        <div className="admin-card-head">
          <div>
            <h1>Domains</h1>
            <p className="admin-muted">
              {user.role === 'admin' ? 'All domains in the whitelist.' : 'Domains you manage.'}
            </p>
          </div>
          {user.role === 'admin' && (
            <button className="admin-btn admin-btn-sm" onClick={onAdd}>+ Add domain</button>
          )}
        </div>
        {domains === null ? (
          <p>Loading…</p>
        ) : domains.length === 0 ? (
          <p className="admin-muted">No domains yet.</p>
        ) : (
          <table className="admin-table">
            <thead><tr><th>Domain</th><th>URL</th><th></th></tr></thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.domain} className={d.archived ? 'admin-row-off' : ''}>
                  <td>{d.domain}{d.archived && <span className="admin-pill">archived</span>}</td>
                  <td className="admin-muted">{d.url || '—'}</td>
                  <td className="admin-row-action">
                    <button className="admin-link-dark" onClick={() => onReport(d.domain)}>Report</button>
                    {!d.archived && <>
                      <span className="admin-dot">·</span>
                      <button className="admin-link-dark" onClick={() => onOpen(d.domain)}>Manage →</button>
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Shell>
  );
}

// Field groups rendered by the editor. `admin` => only admins may edit.
const EDITOR_SECTIONS = [
  { title: 'Site', fields: [
    { key: 'url', label: 'Site URL (CORS origin)', admin: true, placeholder: 'https://artist.com' },
    { key: 'digest_email', label: 'Weekly digest email', placeholder: 'artist@example.com' },
  ]},
  { title: 'Lob templates', fields: [
    { key: 'front_template', label: 'Front (4×6)', placeholder: 'tmpl_…' },
    { key: 'back_template', label: 'Back (4×6)', placeholder: 'tmpl_…' },
    { key: 'front_template6x9', label: 'Front (6×9)', placeholder: 'tmpl_…' },
    { key: 'back_template6x9', label: 'Back (6×9)', placeholder: 'tmpl_…' },
    { key: 'front_template6x11', label: 'Front (6×11)', placeholder: 'tmpl_…' },
    { key: 'back_template6x11', label: 'Back (6×11)', placeholder: 'tmpl_…' },
  ]},
  { title: 'Pricing (cents)', fields: [
    { key: 'cost', label: '4×6 domestic', placeholder: '300' },
    { key: 'cost_international', label: '4×6 international', placeholder: '500' },
    { key: 'cost6x9', label: '6×9 (US only)', placeholder: '' },
    { key: 'cost6x11', label: '6×11 (US only)', placeholder: '' },
  ]},
];

function DomainEditor({ user, onLogout, domain, onBack, onReport, onDeleted, nav }) {
  const [config, setConfig] = useState(null);
  const [orig, setOrig] = useState(null);
  const [settings, setSettings] = useState({ artist_split_pct: 50, stripe_destination: '' });
  const [origSettings, setOrigSettings] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { ok, data } = await adminPost('/admin/domains/get', { domain });
      if (ok && data.result === 'success') {
        setConfig(data.config); setOrig(data.config);
        setSettings(data.settings); setOrigSettings(data.settings);
        setIsAdmin(data.isAdmin);
      } else {
        setError(data.message || 'Could not load domain');
      }
    })();
  }, [domain]);

  const setField = (k, v) => { setConfig((c) => ({ ...c, [k]: v })); setStatus(''); };
  const setSetting = (k, v) => { setSettings((s) => ({ ...s, [k]: v })); setStatus(''); };

  const save = async () => {
    setBusy(true); setError(''); setStatus('');
    // Only send changed fields (avoids tripping admin-only validation on
    // untouched fields like url for non-admin users).
    const configDiff = {};
    Object.keys(config).forEach((k) => { if (config[k] !== orig[k]) configDiff[k] = config[k]; });
    const settingsDiff = {};
    if (String(settings.stripe_destination) !== String(origSettings.stripe_destination)) {
      settingsDiff.stripe_destination = settings.stripe_destination;
    }
    if (isAdmin && Number(settings.artist_split_pct) !== Number(origSettings.artist_split_pct)) {
      settingsDiff.artist_split_pct = Number(settings.artist_split_pct);
    }
    const { ok, data } = await adminPost('/admin/domains/save', { domain, config: configDiff, settings: settingsDiff });
    setBusy(false);
    if (ok && data.result === 'success') {
      setConfig(data.config); setOrig(data.config);
      setSettings(data.settings); setOrigSettings(data.settings);
      setStatus('Saved.');
    } else {
      setError(data.message || 'Save failed');
    }
  };

  return (
    <Shell user={user} onLogout={onLogout} nav={nav}>
      <button className="admin-back" onClick={onBack}>← All domains</button>
      <div className="admin-card">
        <div className="admin-card-head">
          <h1>{domain}</h1>
          <button className="admin-btn admin-btn-sm admin-btn-ghost" onClick={() => onReport(domain)}>View report →</button>
        </div>
        {config === null ? (
          error ? <p className="admin-error">{error}</p> : <p>Loading…</p>
        ) : (
          <>
            {EDITOR_SECTIONS.map((sec) => (
              <div key={sec.title} className="admin-section">
                <h2 className="admin-section-title">{sec.title}</h2>
                <div className="admin-grid">
                  {sec.fields.map((f) => {
                    const locked = f.admin && !isAdmin;
                    return (
                      <Field key={f.key} label={f.label + (locked ? ' (admin only)' : '')}
                             value={config[f.key] ?? ''} placeholder={f.placeholder}
                             disabled={locked}
                             onChange={(e) => setField(f.key, e.target.value)} />
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="admin-section">
              <h2 className="admin-section-title">Payout</h2>
              <div className="admin-grid">
                <Field label="Stripe destination (acct_…)" value={settings.stripe_destination ?? ''}
                       placeholder="acct_…" onChange={(e) => setSetting('stripe_destination', e.target.value)} />
                <Field label={'Artist split %' + (isAdmin ? '' : ' (admin only)')} type="number"
                       min="0" max="100" step="0.01" disabled={!isAdmin}
                       value={settings.artist_split_pct ?? ''}
                       onChange={(e) => setSetting('artist_split_pct', e.target.value)} />
              </div>
            </div>
            {error && <p className="admin-error">{error}</p>}
            {status && <p className="admin-status">{status}</p>}
            <button className="admin-btn" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button>

            {isAdmin && (
              <div className="admin-danger">
                <button className="admin-link-danger" onClick={async () => {
                  if (!window.confirm(`Delete ${domain}? It stops accepting orders and is removed from the whitelist. Its reporting history is kept and stays viewable.`)) return;
                  const { ok, data } = await adminPost('/admin/domains/delete', { domain });
                  if (ok && data.result === 'success') onDeleted();
                  else setError(data.message || 'Could not delete domain');
                }}>Delete domain</button>
                <span className="admin-muted"> — removes it from the whitelist; reporting is kept.</span>
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

const ADD_TEMPLATE_FIELDS = [
  { key: 'front_template', label: 'Front (4×6)' },
  { key: 'back_template', label: 'Back (4×6)' },
  { key: 'front_template6x9', label: 'Front (6×9)' },
  { key: 'back_template6x9', label: 'Back (6×9)' },
  { key: 'front_template6x11', label: 'Front (6×11)' },
  { key: 'back_template6x11', label: 'Back (6×11)' },
];

function AddDomain({ user, onLogout, onCreated, onBack, nav }) {
  const [f, setF] = useState({
    domain: '', url: '',
    front_template: '', back_template: '',
    front_template6x9: '', back_template6x9: '',
    front_template6x11: '', back_template6x11: '',
  });
  const [prefilled, setPrefilled] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Default the templates to the ones already in use; admin can override.
  useEffect(() => {
    adminPost('/admin/domains/defaults', {}).then(({ ok, data }) => {
      if (ok && data.result === 'success') {
        setF((s) => ({ ...s, ...data.templates }));
        setPrefilled(Object.values(data.templates).some(Boolean));
      }
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    const { ok, data } = await adminPost('/admin/domains/create', f);
    setBusy(false);
    if (ok && data.result === 'success') { onCreated(data.domain); }
    else { setError(data.message || 'Could not create domain'); }
  };

  return (
    <Shell user={user} onLogout={onLogout} nav={nav}>
      <button className="admin-back" onClick={onBack}>← All domains</button>
      <div className="admin-card">
        <h1>Add domain</h1>
        <form onSubmit={submit}>
          <div className="admin-grid">
            <Field label="Domain" placeholder="artist.com" value={f.domain} onChange={(e) => set('domain', e.target.value)} required />
            <Field label="Site URL (CORS origin)" placeholder="https://artist.com" value={f.url} onChange={(e) => set('url', e.target.value)} required />
          </div>

          <h2 className="admin-section-title" style={{ marginTop: 22 }}>Lob templates</h2>
          {prefilled && <p className="admin-muted" style={{ margin: '0 0 8px' }}>Defaulted to your existing templates — change any if this domain differs.</p>}
          <div className="admin-grid">
            {ADD_TEMPLATE_FIELDS.map((t) => (
              <Field key={t.key} label={t.label} placeholder="tmpl_…"
                     value={f[t.key]} onChange={(e) => set(t.key, e.target.value)}
                     required={t.key === 'front_template' || t.key === 'back_template'} />
            ))}
          </div>

          {error && <p className="admin-error">{error}</p>}
          <button className="admin-btn" disabled={busy} type="submit">{busy ? 'Creating…' : 'Create domain'}</button>
          <p className="admin-muted" style={{ marginTop: 14 }}>Costs, split and payout can be set after creating. A size only ships when both its templates are filled.</p>
        </form>
      </div>
    </Shell>
  );
}

const money = (cents) => (cents < 0 ? '-' : '') + '$' + (Math.abs(cents) / 100).toFixed(2);
const fmtDate = (unix) => new Date(unix * 1000).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

function ReportView({ user, onLogout, domain, onBack, onManage, nav }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (r) => {
    setBusy(true); setError('');
    const { ok, data } = await adminPost('/admin/report', { domain, from: r.from || undefined, to: r.to || undefined });
    setBusy(false);
    if (ok && data.result === 'success') setReport(data);
    else setError(data.message || 'Could not load report');
  }, [domain]);

  useEffect(() => { load({ from: '', to: '' }); }, [load]);

  const t = report && report.totals;
  return (
    <Shell user={user} onLogout={onLogout} nav={nav}>
      <button className="admin-back" onClick={onBack}>← All domains</button>
      <div className="admin-card">
        <div className="admin-card-head">
          <div>
            <h1>{domain}</h1>
            <p className="admin-muted">
              {report ? <>Orders report · split {report.split_pct}% to artist · Lob plan: {report.lob_plan}</> : 'Orders report'}
            </p>
          </div>
          <button className="admin-btn admin-btn-sm admin-btn-ghost" onClick={() => onManage(domain)}>Manage →</button>
        </div>

        <div className="admin-filter">
          <label>From <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></label>
          <label>To <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></label>
          <button className="admin-btn admin-btn-sm" disabled={busy} onClick={() => load(range)}>{busy ? '…' : 'Apply'}</button>
          {(range.from || range.to) && (
            <button className="admin-link-dark" onClick={() => { setRange({ from: '', to: '' }); load({ from: '', to: '' }); }}>Clear</button>
          )}
        </div>

        {error && <p className="admin-error">{error}</p>}
        {report === null ? (
          <p>Loading…</p>
        ) : report.orders.length === 0 ? (
          <p className="admin-muted">No orders in this period.</p>
        ) : (
          <div className="admin-table-scroll">
            <table className="admin-table admin-report">
              <thead>
                <tr>
                  <th>Date</th><th>Size</th><th>Promo</th>
                  <th className="num">Paid</th><th className="num">Cost</th><th className="num">Net</th>
                  <th className="num">Platform</th><th className="num">Artist</th>
                </tr>
              </thead>
              <tbody>
                {report.orders.map((o) => (
                  <tr key={o.id}>
                    <td>
                      {o.lob_id
                        ? <a className="admin-pclink" href={`/?track=${o.lob_id}`} target="_blank" rel="noopener noreferrer" title="View postcard">{fmtDate(o.created)} ↗</a>
                        : fmtDate(o.created)}
                    </td>
                    <td>{o.size}{o.country !== 'US' ? ` · ${o.country}` : ''}</td>
                    <td className="admin-muted">{o.promo || '—'}</td>
                    <td className="num">{money(o.paid)}</td>
                    <td className="num">{money(o.cost)}</td>
                    <td className={'num' + (o.net < 0 ? ' neg' : '')}>{money(o.net)}</td>
                    <td className={'num' + (o.platform < 0 ? ' neg' : '')}>{money(o.platform)}</td>
                    <td className={'num' + (o.artist < 0 ? ' neg' : '')}>{money(o.artist)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t.count} order{t.count === 1 ? '' : 's'}</td><td></td><td></td>
                  <td className="num">{money(t.paid)}</td>
                  <td className="num">{money(t.cost)}</td>
                  <td className={'num' + (t.net < 0 ? ' neg' : '')}>{money(t.net)}</td>
                  <td className={'num' + (t.platform < 0 ? ' neg' : '')}>{money(t.platform)}</td>
                  <td className={'num' + (t.artist < 0 ? ' neg' : '')}>{money(t.artist)}</td>
                </tr>
              </tfoot>
            </table>
            {report.truncated && <p className="admin-muted">Showing the most recent 2000 orders — narrow the date range to see older ones.</p>}
            <p className="admin-muted admin-fineprint">Cost is Lob's estimated make-and-send price for the {report.lob_plan} plan; net = paid − cost, split {report.split_pct}% to the artist.</p>
          </div>
        )}
      </div>
    </Shell>
  );
}

function InviteForm({ domains, onSent }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [domain, setDomain] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setResult(null);
    const body = role === 'admin' ? { email, role } : { email, role, domain };
    const { ok, data } = await adminPost('/admin/invite/create', body);
    setBusy(false);
    if (ok && data.result === 'success') {
      setResult(data); setEmail(''); setDomain('');
      onSent();
    } else {
      setError(data.message || 'Could not send invite');
    }
  };

  return (
    <form className="admin-invite" onSubmit={submit}>
      <div className="admin-invite-row">
        <input type="email" placeholder="person@example.com" value={email} required
               onChange={(e) => setEmail(e.target.value)} />
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </select>
        {role === 'user' && (
          <select value={domain} required onChange={(e) => setDomain(e.target.value)}>
            <option value="">Choose domain…</option>
            {domains.map((d) => <option key={d.domain} value={d.domain}>{d.domain}</option>)}
          </select>
        )}
        <button className="admin-btn admin-btn-sm" disabled={busy} type="submit">{busy ? 'Sending…' : 'Send invite'}</button>
      </div>
      {error && <p className="admin-error">{error}</p>}
      {result && (
        <p className="admin-status">
          Invited {result.email}{result.domain ? ` for ${result.domain}` : ' (admin)'}.
          {result.email_sent ? ' Email sent.' : ' Email failed — copy this link: '}
          {!result.email_sent && <a href={result.link}>{result.link}</a>}
        </p>
      )}
    </form>
  );
}

function UsersView({ user, onLogout, nav }) {
  const [data, setData] = useState(null);
  const [domains, setDomains] = useState([]);

  const load = useCallback(async () => {
    const [u, d] = await Promise.all([
      adminPost('/admin/users/list', {}),
      adminPost('/admin/domains/list', {}),
    ]);
    setData(u.data); setDomains(d.data.domains || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeUser = async (id, email) => {
    if (!window.confirm(`Delete ${email}? They lose all access.`)) return;
    await adminPost('/admin/users/delete', { user_id: id });
    load();
  };
  const removeDomain = async (id, domain) => {
    await adminPost('/admin/users/remove_domain', { user_id: id, domain });
    load();
  };
  const revokeInvite = async (link) => {
    await adminPost('/admin/invites/revoke', { link });
    load();
  };

  return (
    <Shell user={user} onLogout={onLogout} nav={nav}>
      <div className="admin-card">
        <h1>Invite someone</h1>
        <p className="admin-muted">They’ll get an email with a link to set a password. One domain per invite; the same email can be invited to several.</p>
        <InviteForm domains={domains} onSent={load} />
      </div>

      <div className="admin-card" style={{ marginTop: 20 }}>
        <h1>Users</h1>
        {data === null ? <p>Loading…</p> : (
          <table className="admin-table">
            <thead><tr><th>Email</th><th>Role</th><th>Domains</th><th></th></tr></thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}{u.pending && <span className="admin-pill">pending</span>}</td>
                  <td>{u.role}</td>
                  <td>
                    {u.role === 'admin' ? <span className="admin-muted">all</span> :
                      u.domains.length === 0 ? <span className="admin-muted">—</span> :
                      u.domains.map((d) => (
                        <span key={d} className="admin-chip">{d}
                          <button className="admin-chip-x" title="Revoke" onClick={() => removeDomain(u.id, d)}>×</button>
                        </span>
                      ))}
                  </td>
                  <td className="admin-row-action">
                    {u.id !== user.id && <button className="admin-link-dark" onClick={() => removeUser(u.id, u.email)}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.invites.length > 0 && (
        <div className="admin-card" style={{ marginTop: 20 }}>
          <h1>Pending invites</h1>
          <table className="admin-table">
            <thead><tr><th>Email</th><th>For</th><th>Expires</th><th></th></tr></thead>
            <tbody>
              {data.invites.map((i) => (
                <tr key={i.link}>
                  <td>{i.email}</td>
                  <td>{i.role === 'admin' ? 'admin' : i.domain}</td>
                  <td className="admin-muted">{new Date(i.expires_at.replace(' ', 'T')).toLocaleDateString()}</td>
                  <td className="admin-row-action">
                    <button className="admin-link-dark" onClick={() => navigator.clipboard && navigator.clipboard.writeText(i.link)}>Copy link</button>
                    <span className="admin-dot">·</span>
                    <button className="admin-link-dark" onClick={() => revokeInvite(i.link)}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Shell>
  );
}

function PromoRow({ promo, domains, onSaved, onToggle }) {
  const [sel, setSel] = useState(promo.allowed);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [toggling, setToggling] = useState(false);

  const dirty = sel.slice().sort().join(',') !== promo.allowed.slice().sort().join(',');
  const toggle = (d) => {
    setSaved(false);
    setSel((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);
  };
  const save = async () => {
    setBusy(true);
    const { ok, data } = await adminPost('/admin/promos/save', { promo_id: promo.id, domains: sel });
    setBusy(false);
    if (ok && data.result === 'success') { setSaved(true); onSaved(promo.id, data.allowed); }
  };
  const flip = async () => {
    setToggling(true);
    await onToggle(promo.id, !promo.active);
    setToggling(false);
  };

  return (
    <tr className={promo.active ? '' : 'admin-row-off'}>
      <td><code>{promo.code}</code>{!promo.active && <span className="admin-pill">disabled</span>}</td>
      <td className="admin-muted">{promo.discount}</td>
      <td>
        <div className="admin-sites">
          {domains.map((d) => (
            <label key={d} className="admin-check">
              <input type="checkbox" checked={sel.includes(d)} onChange={() => toggle(d)} /> {d}
            </label>
          ))}
        </div>
        <div className="admin-scope-note">{sel.length === 0 ? 'All sites (unrestricted)' : `Only: ${sel.join(', ')}`}</div>
      </td>
      <td className="admin-row-action">
        {dirty && <button className="admin-btn admin-btn-sm" disabled={busy} onClick={save}>{busy ? '…' : 'Save'}</button>}
        {!dirty && saved && <span className="admin-status">Saved</span>}
        <div>
          <button className="admin-link-dark" disabled={toggling} onClick={flip}>
            {promo.active ? 'Disable' : 'Enable'}
          </button>
        </div>
      </td>
    </tr>
  );
}

function CreatePromo({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [type, setType] = useState('percent');
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    const { ok, data } = await adminPost('/admin/promos/create', { code, discount_type: type, value: Number(value) });
    setBusy(false);
    if (ok && data.result === 'success') {
      onCreated(data.promo);
      setCode(''); setValue(''); setOpen(false);
    } else {
      setError(data.message || 'Could not create code');
    }
  };

  if (!open) return <button className="admin-btn admin-btn-sm" onClick={() => setOpen(true)}>+ New code</button>;
  return (
    <form className="admin-invite" onSubmit={submit}>
      <div className="admin-invite-row">
        <input placeholder="CODE" value={code} required onChange={(e) => setCode(e.target.value.toUpperCase())} style={{ minWidth: 140 }} />
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="percent">% off</option>
          <option value="amount">$ off</option>
        </select>
        <input type="number" min="0" step="0.01" placeholder={type === 'percent' ? '25' : '1.50'} value={value} required
               onChange={(e) => setValue(e.target.value)} style={{ width: 110 }} />
        <button className="admin-btn admin-btn-sm" disabled={busy} type="submit">{busy ? 'Creating…' : 'Create'}</button>
        <button type="button" className="admin-link-dark" onClick={() => { setOpen(false); setError(''); }}>Cancel</button>
      </div>
      <p className="admin-muted" style={{ fontSize: 12, marginTop: 6 }}>New codes apply to all sites; set per-site scope below after creating.</p>
      {error && <p className="admin-error">{error}</p>}
    </form>
  );
}

function PromosView({ user, onLogout, nav }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const { ok, data } = await adminPost('/admin/promos/list', {});
      if (ok && data.result === 'success') setData(data);
      else setError(data.message || 'Could not load promos');
    })();
  }, []);

  const onSaved = (id, allowed) => {
    setData((d) => ({ ...d, promos: d.promos.map((p) => p.id === id ? { ...p, allowed } : p) }));
  };
  const onCreated = (promo) => {
    setData((d) => ({ ...d, promos: [promo, ...d.promos] }));
  };
  const onToggle = async (id, active) => {
    const { ok, data } = await adminPost('/admin/promos/toggle', { promo_id: id, active });
    if (ok && data.result === 'success') {
      setData((d) => ({ ...d, promos: d.promos.map((p) => p.id === id ? { ...p, active } : p) }));
    }
  };

  return (
    <Shell user={user} onLogout={onLogout} nav={nav}>
      <div className="admin-card">
        <div className="admin-card-head">
          <div>
            <h1>Promo codes</h1>
            <p className="admin-muted">Create codes and choose which sites each one works on. Leave all sites unchecked to allow every site (the default).</p>
          </div>
          {data && <CreatePromo onCreated={onCreated} />}
        </div>
        {error && <p className="admin-error">{error}</p>}
        {data === null ? <p>Loading…</p> : data.promos.length === 0 ? (
          <p className="admin-muted">No promo codes yet — create one above.</p>
        ) : (
          <table className="admin-table">
            <thead><tr><th>Code</th><th>Discount</th><th>Works on</th><th></th></tr></thead>
            <tbody>
              {data.promos.map((p) => (
                <PromoRow key={p.id} promo={p} domains={data.domains} onSaved={onSaved} onToggle={onToggle} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Shell>
  );
}

// Sizes shown in the cost editor. 6x9/6x11 are US-only (no intl input).
const COST_SIZES = [
  { size: '4x6', intl: true },
  { size: '6x9', intl: false },
  { size: '6x11', intl: false },
];
const centsToStr = (c) => (c == null || c === '') ? '' : (c / 100).toFixed(2);
const strToCents = (s) => (s === '' || s == null) ? null : Math.round(parseFloat(s) * 100);

function SettingsView({ user, onLogout, nav }) {
  const [s, setS] = useState(null);
  const [plan, setPlan] = useState('');
  const [draft, setDraft] = useState({}); // {size:{domestic:'$str',international:'$str'}}
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadPlanIntoDraft = (data, p) => {
    const pc = (data.costs && data.costs[p]) || {};
    const d = {};
    COST_SIZES.forEach(({ size }) => {
      d[size] = {
        domestic: centsToStr(pc[size] && pc[size].domestic),
        international: centsToStr(pc[size] && pc[size].international),
      };
    });
    setDraft(d);
  };

  useEffect(() => {
    (async () => {
      const { data } = await adminPost('/admin/settings/get', {});
      if (data.result === 'success') { setS(data); setPlan(data.lob_plan); loadPlanIntoDraft(data, data.lob_plan); }
    })();
  }, []);

  const onPlan = (p) => { setPlan(p); setStatus(''); loadPlanIntoDraft(s, p); };
  const setCost = (size, key, val) => {
    setStatus('');
    setDraft((d) => ({ ...d, [size]: { ...d[size], [key]: val } }));
  };

  const save = async () => {
    setBusy(true); setStatus(''); setError('');
    const costs = {};
    COST_SIZES.forEach(({ size, intl }) => {
      const row = {};
      const dom = strToCents(draft[size].domestic);
      if (dom != null && !isNaN(dom)) row.domestic = dom;
      if (intl) { const i = strToCents(draft[size].international); if (i != null && !isNaN(i)) row.international = i; }
      costs[size] = row;
    });
    const { ok, data } = await adminPost('/admin/settings/save', { lob_plan: plan, costs });
    setBusy(false);
    if (ok && data.result === 'success') { setS(data); setStatus('Saved.'); }
    else { setError(data.message || 'Save failed'); }
  };

  return (
    <Shell user={user} onLogout={onLogout} nav={nav}>
      <div className="admin-card admin-narrow">
        <h1>Settings</h1>
        {s === null ? <p>Loading…</p> : (
          <>
            <label className="admin-field">
              <span>Active Lob plan</span>
              <select value={plan} onChange={(e) => onPlan(e.target.value)}>
                {s.plans.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>

            <h2 className="admin-section-title" style={{ marginTop: 22 }}>Lob cost per postcard ($)</h2>
            <table className="admin-table">
              <thead><tr><th>Size</th><th className="num">US</th><th className="num">International</th></tr></thead>
              <tbody>
                {COST_SIZES.map(({ size, intl }) => (
                  <tr key={size}>
                    <td>{size}</td>
                    <td className="num">
                      <input className="admin-cost-input" type="number" min="0" step="0.01" placeholder="0.00"
                             value={draft[size]?.domestic ?? ''} onChange={(e) => setCost(size, 'domestic', e.target.value)} />
                    </td>
                    <td className="num">
                      {intl ? (
                        <input className="admin-cost-input" type="number" min="0" step="0.01" placeholder="0.00"
                               value={draft[size]?.international ?? ''} onChange={(e) => setCost(size, 'international', e.target.value)} />
                      ) : <span className="admin-muted">US only</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="admin-muted admin-fineprint">What Lob charges you to print &amp; mail each card — drives the “cost” / net / shares in reports. Enter your real per-card prices from your Lob invoice.</p>
            {error && <p className="admin-error">{error}</p>}
            {status && <p className="admin-status">{status}</p>}
            <button className="admin-btn" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          </>
        )}
      </div>
    </Shell>
  );
}

export default function AdminApp() {
  const path = window.location.pathname;
  const inviteMatch = path.match(/^\/invite\/([a-f0-9]{64})\/?$/i);

  const [auth, setAuth] = useState({ loading: true, user: null });
  const [nav, setNav] = useState({ view: 'list' }); // {view:'list'|'domain'|'report'|'new'|'users'|'settings', domain?}

  const checkAuth = useCallback(async () => {
    const { ok, data } = await adminPost('/admin/me', {});
    setAuth({ loading: false, user: ok && data.result === 'success' ? data.user : null });
  }, []);

  useEffect(() => {
    if (!inviteMatch) { checkAuth(); }
  }, [inviteMatch, checkAuth]);

  const logout = async () => {
    await adminPost('/admin/logout', {});
    window.location.assign('/admin');
  };

  // Invite acceptance is its own public flow.
  if (inviteMatch) return <InviteAccept token={inviteMatch[1]} />;

  if (auth.loading) return <Shell><div className="admin-card admin-narrow">Loading…</div></Shell>;
  if (!auth.user) return <Login onAuthed={(user) => setAuth({ loading: false, user })} />;

  // Nav bar (admins only). 'domains' is active for the whole domains sub-tree.
  const isAdmin = auth.user.role === 'admin';
  const activeTab = ['users', 'settings', 'promos'].includes(nav.view) ? nav.view : 'domains';
  const navBar = {
    isAdmin,
    active: activeTab,
    onNavigate: (tab) => setNav({ view: tab === 'domains' ? 'list' : tab }),
  };
  const props = { user: auth.user, onLogout: logout, nav: navBar };
  const toList = () => setNav({ view: 'list' });

  if (nav.view === 'promos' && isAdmin) return <PromosView {...props} />;
  if (nav.view === 'users' && isAdmin) return <UsersView {...props} />;
  if (nav.view === 'settings' && isAdmin) return <SettingsView {...props} />;
  if (nav.view === 'domain') {
    return <DomainEditor {...props} domain={nav.domain} onBack={toList}
                         onReport={(domain) => setNav({ view: 'report', domain })}
                         onDeleted={toList} />;
  }
  if (nav.view === 'report') {
    return <ReportView {...props} domain={nav.domain} onBack={toList}
                       onManage={(domain) => setNav({ view: 'domain', domain })} />;
  }
  if (nav.view === 'new') {
    return <AddDomain {...props} onBack={toList}
                      onCreated={(domain) => setNav({ view: 'domain', domain })} />;
  }
  return <Dashboard {...props}
                    onOpen={(domain) => setNav({ view: 'domain', domain })}
                    onReport={(domain) => setNav({ view: 'report', domain })}
                    onAdd={() => setNav({ view: 'new' })} />;
}
